import { Injectable } from '@angular/core';
import { PaginatedPricingStore, LoadType } from '../shared/paginated-pricing.store';
import { PricingCategory, PricingOrder, PricingSort, ScrapeResponseData } from '../shared/pricing.types';

export type { LoadType };

@Injectable({ providedIn: 'root' })
export class PricesService extends PaginatedPricingStore {
  override readonly type: LoadType = 0;
  protected override readonly errorMessage = 'Failed to load prices';
  protected override readonly defaultSort: PricingSort | null = null;
  protected override readonly defaultOrder: PricingOrder | null = null;

  constructor() {
    super();
  }

  async addSkin(data: {
    name: string;
    price: number;
    image?: string;
    link?: string;
    category?: PricingCategory;
  }): Promise<void> {
    try {
      // Lowercase name at the emit boundary so the BE stores a single
      // canonical form; URLs (image/link) stay as-is because Steam Market
      // paths are case-sensitive.
      const msg = await this.api.emit<string>('ap_add_skin', {
        ...data,
        name: data.name.toLowerCase(),
      });
      this.toastr.info(msg || 'Skin added');
      await this.loadItems();
    } catch (e: any) {
      this.toastr.error(e?.message ?? 'Failed to add skin');
    }
  }

  async editSkin(id: number, edits: Partial<{
    name: string;
    price: number;
    image: string;
    link: string;
    category: PricingCategory;
  }>): Promise<void> {
    if (Object.keys(edits).length === 0) {
      this.toastr.info('No changes to save');
      return;
    }
    try {
      const normalizedEdits = edits.name !== undefined
        ? { ...edits, name: edits.name.toLowerCase() }
        : edits;
      const msg = await this.api.emit<string>('ap_edit_skin', {
        id,
        edits: normalizedEdits,
      });
      this.toastr.info(msg || 'Skin updated');
      await this.loadItems();
    } catch (e: any) {
      this.toastr.error(e?.message ?? 'Failed to edit skin');
    }
  }

  async scrapeSkin(name: string, link: string): Promise<ScrapeResponseData | null> {
    try {
      const res = await this.api.emit<ScrapeResponseData>(
        'ap_scrape_skin',
        { name: name.toLowerCase(), link },
        null,
      );
      return res ?? null;
    } catch (e: any) {
      this.toastr.error(e?.message ?? 'Failed to scrape skin');
      return null;
    }
  }
}
